import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X } from 'lucide-react';
import { billingAPI } from '../../services/billing';
import toast from 'react-hot-toast';

const STRIPE_PUBLISHABLE_KEY = String(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

function Inner({ onClose, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment failed');
        setLoading(false);
        return;
      }

      toast.success('Payment submitted. We will confirm shortly.');
      onPaid();
      onClose();
    } catch (err) {
      console.warn(err);
      toast.error('Payment failed');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 border rounded">
        <PaymentElement />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={loading || !stripe} className="btn-primary">{loading ? 'Processing...' : 'Pay Now'}</button>
      </div>
    </form>
  );
}

export default function InvoicePayModal({ invoiceId, onClose, onPaid }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState('');

  useEffect(() => {
    const create = async () => {
      if (!stripePromise) {
        setInitError('Stripe is not configured. Set VITE_STRIPE_PUBLISHABLE_KEY in frontend/.env and restart the frontend.');
        return;
      }

      try {
        setLoading(true);
        const { data } = await billingAPI.createPaymentIntent({ invoiceId });
        setClientSecret(data.clientSecret);
      } catch (err) {
        console.warn(err);
        const message = err?.response?.data?.error || err?.response?.data?.details || 'Failed to initialize payment';
        setInitError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };
    create();
  }, [invoiceId]);

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-medium">Pay Invoice</h3>
          <button onClick={onClose} className="text-gray-500"><X size={18} /></button>
        </div>

        {initError ? (
          <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {initError}
          </div>
        ) : !clientSecret ? (
          <div className="text-center py-8">Preparing payment...</div>
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <Inner onClose={onClose} onPaid={onPaid} />
          </Elements>
        )}
      </div>
    </div>
  );
}
